

// _______________________________ Working _______________________________________
import React, { useState, useEffect } from "react";
import "../CSS/contracts.css";
import { FaEye, FaCommentAlt, FaTimes, FaSpinner } from "react-icons/fa";
import Sidebar from "./Sidebar";
import axios from "axios";
import { BASE_API_URL } from "../constants";
import ContractsListPage from "./ContractsListPage";

const ContractManagement = () => {
  const [contracts, setContracts] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [agreementStatus, setAgreementStatus] = useState({});
  const [activeFilter, setActiveFilter] = useState("Prepare Contract");
  const [signingLoading, setSigningLoading] = useState({});
  const [signedContracts, setSignedContracts] = useState({});

  // ------------------ Fetch Contracts ------------------
  useEffect(() => {
    const fetchContracts = async () => {
      const userId = sessionStorage.getItem("userId");
      if (!userId) return;

      try {
        const res = await fetch(`${BASE_API_URL}/api/contracts/${userId}`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const data = await res.json();
        const contractList = data.contracts || [];
        setContracts(contractList);

        //  After getting contracts, check signed status for each
        await checkAllContractsSigned(contractList, userId);
      } catch (err) {
        console.error(" Error fetching contracts:", err);
        setContracts([]);
      }
    };

    fetchContracts();
  }, []);

  // ------------------ Check Signed Status ------------------
  const checkAllContractsSigned = async (contractList, userId) => {
    const signedStatusMap = {};

    for (const contract of contractList) {
      try {
        const response = await axios.post(`${BASE_API_URL}/api/contracts/check-signed`, {
          transactionId: contract.transactionId,
          userId: String(userId),
        });

        signedStatusMap[contract.transactionId] = response.data.contract_signed;
      } catch (err) {
        console.error("Error checking contract sign status:", err);
        signedStatusMap[contract.transactionId] = false;
      }
    }

    setSignedContracts(signedStatusMap);
  };

  // ------------------ Handle Sign Contract ------------------
  const handleSignerSubmit = async (contract) => {
    const transactionId = contract.transactionId;
    const userId = sessionStorage.getItem("userId");

    if (!userId) {
      alert(" User ID missing. Please log in again.");
      return;
    }

    try {
      setSigningLoading((prev) => ({ ...prev, [transactionId]: true }));

      const payload = {
        name: contract.name,
        email: contract.email,
        userId: userId,
        transactionId: transactionId,
      };

      const response = await fetch(`${BASE_API_URL}/api/contracts/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.agreement_status) {
          setAgreementStatus((prev) => ({
            ...prev,
            [transactionId]: data.agreement_status,
          }));
        }

        //  Store document ID for future use
        if (data.id || data.document_id) {
          const documentId = data.id || data.document_id;
          sessionStorage.setItem("document_id", documentId);
          console.log(" Document ID saved to sessionStorage:", documentId);
          alert(` Sign request initiated! Document ID: ${documentId}`);
        } else if (data.kyc_required) {
          alert(data.message || " KYC verification required before signing.");
        } else {
          alert(" Sign request sent successfully.");
        }

        //  After successful sign, mark as signed
        setSignedContracts((prev) => ({ ...prev, [transactionId]: true }));
      } else {
        alert(` Error: ${data.detail || "Sign request failed."}`);
      }
    } catch (error) {
      console.error("Sign request error:", error);
      alert(" Failed to start signing process. Try again later.");
    } finally {
      setSigningLoading((prev) => ({ ...prev, [transactionId]: false }));
    }
  };

  // ------------------ Handle PDF View ------------------
  const handleViewContract = async (transactionId) => {
    try {
      if (!transactionId) {
        alert("Missing transaction ID.");
        return;
      }

      setLoadingPdf(true);
      setShowPdfModal(true);
      setPdfUrl(null);

      const userId = sessionStorage.getItem("userId");

      // Step A: Try fetching the already-saved signed contract
      let pdfBase64 = null;
      try {
        const savedRes = await axios.get(
          `${BASE_API_URL}/api/contracts/view/${transactionId}`
        );
        if (savedRes.data?.pdf_base64) {
          pdfBase64 = savedRes.data.pdf_base64;
        }
      } catch (savedErr) {
        // Not found in DB yet — will try preview fallback
        console.log("Saved contract not found, trying preview...");
      }

      // Step B: If no saved contract, generate a preview on-the-fly
      if (!pdfBase64) {
        const previewRes = await axios.get(
          `${BASE_API_URL}/api/contracts/preview/${transactionId}`,
          { params: { user_id: userId } }
        );
        if (previewRes.data?.pdf_base64) {
          pdfBase64 = previewRes.data.pdf_base64;
        }
      }

      // Step C: Render PDF
      if (pdfBase64) {
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        alert("No contract preview available for this transaction.");
        setShowPdfModal(false);
      }
    } catch (err) {
      console.error("Error fetching contract:", err);
      alert("Could not load contract. Please try again.");
      setShowPdfModal(false);
    } finally {
      setLoadingPdf(false);
    }
  };


  const filters = ["Prepare Contract", "Contract Status"];

  return (
    <>
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="cm-app-container">
        <main className="cm-main-content">
          <div className="page-header">
            <h1 className="page-title">Contract Management</h1>

            <div className="filters-container">
              {filters.map((filter, i) => (
                <button
                  key={i}
                  className={`filter-btn ${activeFilter === filter ? "active" : ""}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* ================= All Contracts Section ================= */}
          {activeFilter === "Prepare Contract" && (
            <div className="contracts-grid fade-in">
              {contracts.length > 0 ? (
                contracts.map((contract, idx) => {
                  const isSigning = signingLoading[contract.transactionId];
                  const isAlreadySigned = signedContracts[contract.transactionId];
                  const status =
                    agreementStatus[contract.transactionId]?.toLowerCase() === "signed"
                      ? "status-signed"
                      : "status-draft";

                  return (
                    <div key={idx} className="contract-card">
                      <span className={`contract-status ${status}`}>
                        {agreementStatus[contract.transactionId] || "Draft"}
                      </span>

                      <div className="contract-header">
                        <h2 className="contract-title">Contribution Terms</h2>
                        <div className="contract-project-details">
                          <div>Project ID: {contract.projectId}</div>
                          <div>Title: {contract.title}</div>
                          <div>Transaction ID: {contract.transactionId}</div>
                        </div>
                      </div>

                      <div className="contract-details">
                        <div className="detail-card">
                          <div className="detail-label">Created Date</div>
                          <div className="detail-value">
                            {contract.createdAt
                              ? new Date(contract.createdAt).toLocaleString()
                              : "N/A"}
                          </div>
                        </div>
                        <div className="detail-card">
                          <div className="detail-label">Amount</div>
                          <div className="detail-value">{contract.amount || 0}</div>
                        </div>
                        <div className="detail-card">
                          <div className="detail-label">Profit Sharing</div>
                          <div className="detail-value">
                            {contract.profitPlan || "N/A"}%
                          </div>
                        </div>
                        <div className="detail-card">
                          <div className="detail-label">Duration</div>
                          <div className="detail-value">{contract.timeline || "N/A"}</div>
                        </div>
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label>Name</label>
                          <input type="text" className="form-control" value={contract.name || ""} readOnly />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input type="email" className="form-control" value={contract.email || ""} readOnly />
                        </div>
                      </div>

                      <div className="action-buttons">
                        <button
                          className="btn btn-outline review-btn"
                          onClick={() => handleViewContract(contract.transactionId)}
                        >
                          <FaEye /> Review
                        </button>

                        {isAlreadySigned ? (
                          <button className="btn btn-secondary" disabled>
                            Signing Started
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary"
                            onClick={() => handleSignerSubmit(contract)}
                            disabled={isSigning}
                          >
                            {isSigning ? (
                              <>
                                <FaSpinner className="spin" /> Signing...
                              </>
                            ) : (
                              <>
                                <FaCommentAlt /> Sign Contract
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p>No contracts available for signing.</p>
              )}
            </div>
          )}

          {activeFilter === "Contract Status" && <ContractsListPage />}
        </main>
      </div>

      {/* ---------------- Stylish PDF Modal ---------------- */}
      {showPdfModal && (
        <div className="pdf-modal-overlay fade-in">
          <div className="pdf-modal fancy">
            <button className="pdf-close-btn" onClick={() => setShowPdfModal(false)}>
              <FaTimes />
            </button>

            <h2 className="pdf-modal-title"> Contract Preview</h2>
            <hr className="pdf-divider" />

            {loadingPdf ? (
              <div className="pdf-loading">
                <div className="spinner"></div>
                <span>Loading...</span>
              </div>
            ) : pdfUrl ? (
              <iframe src={pdfUrl} title="Contract PDF" className="pdf-viewer-modal" />
            ) : (
              <div className="pdf-loading">No PDF available</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ContractManagement;
